import React, { useState, useRef } from "react";
import {
  UploadSimple,
  FilePdf,
  FileImage,
  X,
  CircleNotch,
  CheckCircle,
  WarningCircle,
  ArrowSquareOut,
  Sparkle
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, eur } from "../lib/api";

const VALID_EXTS = [".pdf", ".jpg", ".jpeg", ".png", ".webp"];

export function UploadModal({ open, onClose, onSuccess, onOpenFactura }) {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  if (!open) return null;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (f) => {
    const ext = "." + f.name.split(".").pop().toLowerCase();
    if (!VALID_EXTS.includes(ext)) {
      toast.error(`Formato no compatible (${ext}). Sube un PDF o imagen JPG/PNG.`);
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    const tid = toast.loading("Procesando documento con Gemini Flash y archivando en Drive/Sheets...");

    try {
      const data = await api.uploadFactura(file);
      setResult(data);
      toast.success(data.message || "Factura procesada con éxito", { id: tid });
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Error al procesar la factura.";
      toast.error(msg, { id: tid });
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isPdf = file?.name?.toLowerCase().endsWith(".pdf");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-150">
      <div
        className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-lg overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-slate-900 text-white rounded-xl shadow-sm">
              <UploadSimple size={18} weight="bold" />
            </div>
            <div>
              <h2 className="font-display font-bold text-slate-900 text-base">
                Subir Factura Manual
              </h2>
              <p className="text-xs text-slate-400">
                Extracción automática con IA, renombrado, Drive y Sheets
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={uploading}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {!result ? (
            <>
              {/* Dropzone */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => !file && fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                  dragActive
                    ? "border-slate-900 bg-slate-50 scale-[0.99]"
                    : file
                    ? "border-slate-300 bg-slate-50/60"
                    : "border-slate-200 hover:border-slate-400 hover:bg-slate-50/50 cursor-pointer"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={handleChange}
                  className="hidden"
                />

                {file ? (
                  <div className="flex items-center justify-between gap-3 text-left">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-3 bg-white border border-slate-200 rounded-xl shrink-0 text-slate-700 shadow-sm">
                        {isPdf ? (
                          <FilePdf size={28} className="text-rose-500" />
                        ) : (
                          <FileImage size={28} className="text-sky-500" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-sm text-slate-900 truncate">
                          {file.name}
                        </div>
                        <div className="text-xs text-slate-400 font-mono">
                          {(file.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    </div>
                    {!uploading && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReset();
                        }}
                        className="text-xs text-slate-400 hover:text-rose-600 px-2 py-1 rounded border border-slate-200 hover:border-rose-200 transition-colors"
                      >
                        Cambiar
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="inline-flex p-3 rounded-full bg-slate-100 text-slate-600 mb-1">
                      <UploadSimple size={24} />
                    </div>
                    <div className="text-sm font-semibold text-slate-800">
                      Arrastra y suelta tu factura aquí
                    </div>
                    <div className="text-xs text-slate-400">
                      o haz clic para seleccionar (PDF, JPG, PNG hasta 25MB)
                    </div>
                  </div>
                )}
              </div>

              {/* Proceso Informativo */}
              <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100 text-xs text-slate-500 space-y-1.5">
                <div className="flex items-center gap-1.5 font-medium text-slate-700">
                  <Sparkle size={14} className="text-purple-600" weight="fill" />
                  ¿Qué hará el sistema automáticamente?
                </div>
                <ul className="list-disc pl-4 space-y-0.5 text-[11px] text-slate-500">
                  <li>Extraerá emisor, CIF, nº de factura y desglose de IVA con Gemini Flash.</li>
                  <li>Renombrará el archivo con el estándar <code>AAAA-MM-DD_PROVEEDOR_NUM_TOTAL.pdf</code>.</li>
                  <li>Lo guardará ordenado en Google Drive y añadirá la fila en Google Sheets.</li>
                </ul>
              </div>
            </>
          ) : (
            /* Resultado de la extracción */
            <div className="space-y-4 animate-in fade-in zoom-in-95 duration-150">
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 space-y-3">
                <div className="flex items-center gap-2 font-bold text-sm text-emerald-800">
                  <CheckCircle size={20} weight="fill" className="text-emerald-600" />
                  Factura procesada y archivada
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs pt-1 border-t border-emerald-200/60">
                  <div>
                    <span className="text-emerald-700 block text-[10px] uppercase font-semibold">Proveedor</span>
                    <span className="font-bold text-slate-900">{result.detalle?.proveedor || "—"}</span>
                  </div>
                  <div>
                    <span className="text-emerald-700 block text-[10px] uppercase font-semibold">Nº Factura</span>
                    <span className="font-mono font-medium text-slate-900">{result.detalle?.num_factura || "S/N"}</span>
                  </div>
                  <div>
                    <span className="text-emerald-700 block text-[10px] uppercase font-semibold">Importe Total</span>
                    <span className="font-bold text-slate-900 text-sm">{eur(result.detalle?.total)}</span>
                  </div>
                  <div>
                    <span className="text-emerald-700 block text-[10px] uppercase font-semibold">Estado</span>
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      result.detalle?.estado === "OK" ? "bg-emerald-200 text-emerald-900" : "bg-amber-200 text-amber-900"
                    }`}>
                      {result.detalle?.estado}
                    </span>
                  </div>
                </div>

                <div className="text-[11px] text-emerald-800 font-mono bg-emerald-100/60 p-2 rounded truncate">
                  📁 {result.detalle?.nombre_archivo}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between gap-3">
          {!result ? (
            <>
              <button
                onClick={onClose}
                disabled={uploading}
                className="px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm transition-all"
              >
                {uploading ? (
                  <>
                    <CircleNotch size={14} className="animate-spin" />
                    Procesando con Gemini...
                  </>
                ) : (
                  <>
                    <UploadSimple size={14} weight="bold" />
                    Procesar Factura
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
              >
                Subir otra factura
              </button>
              <div className="flex items-center gap-2">
                {onOpenFactura && result.factura && (
                  <button
                    onClick={() => {
                      onClose();
                      onOpenFactura(result.factura);
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg shadow-sm transition-colors"
                  >
                    <ArrowSquareOut size={14} />
                    Ver Ficha
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
